if (!customElements.get('product-cut-wizard')) {
  customElements.define(
    'product-cut-wizard',
    class ProductCutWizard extends HTMLElement {
      constructor() {
        super();
        this._savedSelections = null;
        this._currentIndex = 0;
        this._draftSelections = [];
        this._unsubVariant = null;
        this._submitGuardAbort = null;
      }

      connectedCallback() {
        this.blockId = this.dataset.blockId;
        this.formId = this.dataset.productFormId;
        this.propertyPrefix = this.dataset.propertyPrefix || 'Cut';
        this.requireBeforeAdd = this.dataset.requireBeforeAdd === 'true';
        this.currency = this.dataset.currency || 'USD';
        this.locale = this.dataset.locale || undefined;

        const jsonEl = this.querySelector(`script[type="application/json"][id^="CutWizardConfig-"]`);
        try {
          this.config = jsonEl ? JSON.parse(jsonEl.textContent) : null;
        } catch (e) {
          this.config = null;
        }

        this._baseSteps = Array.isArray(this.config?.steps)
          ? JSON.parse(JSON.stringify(this.config.steps))
          : [];

        const variantsEl = this.querySelector('script[id^="CutWizardVariants-"]');
        try {
          this._variantList = variantsEl ? JSON.parse(variantsEl.textContent) : [];
        } catch (e) {
          this._variantList = [];
        }

        this._packSize = 'half';
        this.steps = [];

        this.modal = document.querySelector(`#CutWizardModal-${this.blockId}`);
        this.summaryList = this.querySelector('[data-cut-wizard-summary-list]');
        this.summaryEmpty = this.querySelector('[data-cut-wizard-summary-empty]');
        this.totalExtrasEl = this.querySelector('[data-cut-wizard-total-extras]');
        this.totalCombinedEl = this.querySelector('[data-cut-wizard-total-combined]');
        this.packPriceEl = this.querySelector('[data-cut-wizard-pack-price]');
        this.totalsRow = this.querySelector('[data-cut-wizard-totals]');
        this.summaryFootnote = this.querySelector('[data-cut-wizard-summary-footnote]');
        this.refreshProductFormRef();

        this.modalTitleEl = this.modal?.querySelector('[data-wizard-modal-title]');
        this.modalBody = this.modal?.querySelector('[data-cut-wizard-modal-body]');
        this.btnBack = this.modal?.querySelector('[data-wizard-back]');
        this.btnNext = this.modal?.querySelector('[data-wizard-next]');
        this.btnSave = this.modal?.querySelector('[data-wizard-save]');
        this.runningTotalEl = this.modal?.querySelector('[data-wizard-running-total]');

        this.btnBack?.addEventListener('click', () => this.goStep(-1));
        this.btnNext?.addEventListener('click', () => this.goStep(1));
        this.btnSave?.addEventListener('click', () => this.saveAndClose());

        this.querySelector('[data-cut-wizard-open]')?.addEventListener('click', () => this.openModal(false));
        this.querySelector('[data-cut-wizard-edit]')?.addEventListener('click', () => this.openModal(true));

        if (typeof subscribe === 'function') {
          this._unsubVariant = subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
            const sectionId = payload?.data?.sectionId;
            const html = payload?.data?.html;
            const pi = this.closest('product-info');
            const sid = pi?.dataset.originalSection || pi?.dataset.section;
            if (!pi || sectionId !== sid) return;
            if (!html?.querySelector?.(`product-cut-wizard[data-block-id="${this.blockId}"]`)) return;
            this.applyPackSizeAndSteps(payload?.data?.variant);
            this.resetWizardState();
            requestAnimationFrame(() => this.hidePackOptionLegend());
          });
        }

        this.applyPackSizeAndSteps(null);

        if (this.steps.length === 0) {
          this.classList.add('cut-wizard--disabled');
          requestAnimationFrame(() => this.hidePackOptionLegend());
          return;
        }

        this.scheduleSyncAddToCartGate();
        this.attachSubmitGuard();

        const pi = this.closest('product-info');
        pi?.addEventListener('product-info:loaded', () => this.scheduleSyncAddToCartGate(), { once: true });

        requestAnimationFrame(() => this.hidePackOptionLegend());
      }

      disconnectedCallback() {
        this._unsubVariant?.();
        this._unsubVariant = null;
        this._submitGuardAbort?.abort();
        this._submitGuardAbort = null;
        const form = document.getElementById(this.formId);
        if (form?.dataset.cutWizardSubmitGuard) delete form.dataset.cutWizardSubmitGuard;
      }

      refreshProductFormRef() {
        this.productForm = document.getElementById(this.formId)?.closest('product-form');
      }

      scheduleSyncAddToCartGate() {
        const run = () => this.syncAddToCartGate();
        queueMicrotask(run);
        requestAnimationFrame(run);
        customElements.whenDefined('product-form').then(run);
        setTimeout(run, 0);
        setTimeout(run, 100);
      }

      syncAddToCartGate() {
        if (!this.requireBeforeAdd || this.steps.length === 0) return;
        this.setAddToCartGate(!this.completeDraftFromSaved());
      }

      attachSubmitGuard() {
        const bind = () => {
          const form = document.getElementById(this.formId);
          if (!form || form.dataset.cutWizardSubmitGuard === 'true') return;
          form.dataset.cutWizardSubmitGuard = 'true';
          this._submitGuardAbort?.abort();
          this._submitGuardAbort = new AbortController();
          const { signal } = this._submitGuardAbort;
          form.addEventListener(
            'submit',
            (e) => {
              if (!this.requireBeforeAdd || this.steps.length === 0) return;
              if (!this.isVariantPurchasable()) return;
              if (this.completeDraftFromSaved()) return;
              e.preventDefault();
              e.stopImmediatePropagation();
              this.syncAddToCartGate();
            },
            { capture: true, signal }
          );
        };
        queueMicrotask(bind);
        requestAnimationFrame(bind);
        customElements.whenDefined('product-form').then(bind);
        setTimeout(bind, 0);
      }

      hidePackOptionLegend() {
        const root = this.closest('product-info');
        if (!root) return;
        root
          .querySelectorAll(
            'variant-selects fieldset legend.form__label, variant-selects .product-form__input--dropdown label.form__label'
          )
          .forEach((el) => {
            const name = el.textContent.split(':')[0].trim();
            if (/^pack$/i.test(name)) el.classList.add('visually-hidden');
          });
      }

      updateRunningTotal() {
        if (!this.runningTotalEl) return;
        const base = parseInt(this.dataset.basePriceCents || '0', 10) || 0;
        let extras = 0;
        const n = this.steps.length;
        for (let i = 0; i < n; i++) {
          const sel = this._draftSelections[i];
          if (sel == null) continue;
          extras += Number(this.steps[i]?.options?.[sel]?.extra_cents) || 0;
        }
        const label = this.dataset.runningTotalLabel || 'Total';
        this.runningTotalEl.textContent = `${label}: ${this.formatMoney(base + extras)}`;
      }


      partHeading(baseHeading, partIndex) {
        const raw = String(baseHeading || 'Part').trim();
        const base = raw.replace(/\s+#\d+\s*$/i, '').trim() || 'Part';
        if (partIndex === 1) return `${base} #1`;
        return `${base} #2`;
      }

      cloneStep(step, newHeading, idSuffix) {
        const o = {
          ...step,
          heading: newHeading,
          options: (step.options || []).map((op) => ({ ...op })),
        };
        if (idSuffix != null) {
          o.id = step.id != null && step.id !== '' ? `${step.id}_${idSuffix}` : undefined;
        }
        return o;
      }

      baseHeadingForFull(baseHeading) {
        const raw = String(baseHeading || 'Part').trim();
        return raw.replace(/\s+#\d+\s*$/i, '').trim() || 'Part';
      }

      expandSteps(baseSteps, packSize) {
        if (!Array.isArray(baseSteps)) return [];
        if (packSize !== 'full') {
          // Half: use headings exactly as in JSON (no auto #1 / #2).
          return baseSteps.map((step, si) =>
            this.cloneStep(step, String(step.heading || `Part ${si + 1}`).trim(), null)
          );
        }
        const out = [];
        baseSteps.forEach((step, si) => {
          const baseH = step.heading || `Part ${si + 1}`;
          const shouldDuplicate = step?.duplicate_for_full !== false;
          if (!shouldDuplicate) {
            out.push(this.cloneStep(step, this.baseHeadingForFull(baseH), null));
            return;
          }
          out.push(this.cloneStep(step, this.partHeading(baseH, 1), 1));
          out.push(this.cloneStep(step, this.partHeading(baseH, 2), 2));
        });
        return out;
      }

      detectPackSize(variant) {
        if (this.dataset.doubleCutsForFull !== 'true') return 'half';
        const opts = variant?.options;
        if (!Array.isArray(opts) || opts.length === 0) return 'half';
        const optIdx = Math.max(
          0,
          Math.min(opts.length - 1, parseInt(this.dataset.packOptionIndex || '1', 10) - 1)
        );
        const val = opts[optIdx];
        const fullLabel = (this.dataset.fullOptionValue || 'Full').trim().toLowerCase();
        if (val != null && String(val).trim().toLowerCase() === fullLabel) return 'full';
        return 'half';
      }

      getCurrentVariantFromDom() {
        const form = document.getElementById(this.formId);
        let vid = parseInt(form?.querySelector('input[name="id"]')?.value, 10);
        if (!Number.isFinite(vid) && this.dataset.initialVariantId) {
          vid = parseInt(this.dataset.initialVariantId, 10);
        }
        if (!Number.isFinite(vid)) return null;
        return this._variantList?.find((v) => v.id === vid) || null;
      }

      applyPackSizeAndSteps(variant) {
        let v = variant || this.getCurrentVariantFromDom();
        if (v?.id && this._variantList?.length) {
          const row = this._variantList.find((x) => x.id === v.id);
          if (row) {
            v = {
              ...row,
              ...v,
              options: Array.isArray(v.options) && v.options.length ? v.options : row.options,
              price: v.price != null ? v.price : row.price,
            };
          }
        }
        const pack = this.detectPackSize(v);
        this._packSize = pack;
        this.steps = this.expandSteps(this._baseSteps, pack);
        if (v?.price != null) {
          this.dataset.basePriceCents = String(v.price);
        }
        this.renderModalChrome();
      }

      resetWizardState() {
        this._savedSelections = null;
        this._draftSelections = [];
        this._currentIndex = 0;
        this.clearPropertyInputs();
        this.updateSummaryDisplay();
        this.syncAddToCartGate();
      }

      isVariantPurchasable() {
        const form = document.getElementById(this.formId);
        const input = form?.querySelector('input[name="id"]');
        return !!(input && !input.hasAttribute('disabled'));
      }

      formatMoney(cents) {
        const n = Number(cents) || 0;
        try {
          return new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.currency,
          }).format(n / 100);
        } catch (e) {
          return (n / 100).toFixed(2);
        }
      }

      formatExtra(cents) {
        const n = Number(cents) || 0;
        if (n === 0) return '';
        return `+${this.formatMoney(n)}`;
      }

      includedLabel() {
        return (this.dataset.labelIncluded || 'Included').trim() || 'Included';
      }

      getIncludedExtras() {
        const ie = this.config?.included_extras;
        if (!ie || !Array.isArray(ie.items) || ie.items.length === 0) return null;
        const items = ie.items
          .map((item) => {
            if (item == null) return '';
            if (typeof item === 'string') return item.trim();
            return String(item.label || '').trim();
          })
          .filter(Boolean);
        if (items.length === 0) return null;
        return {
          heading: String(ie.heading || 'Extra').trim() || 'Extra',
          items,
        };
      }

      appendIncludedSummaryRows() {
        const inc = this.getIncludedExtras();
        if (!inc || !this.summaryList) return false;
        const tag = this.includedLabel();
        inc.items.forEach((itemLabel) => {
          const li = document.createElement('li');
          li.className = 'cut-wizard__summary-item cut-wizard__summary-item--included';
          const part = document.createElement('span');
          part.className = 'cut-wizard__summary-part';
          part.textContent = inc.heading;
          const choice = document.createElement('span');
          choice.className = 'cut-wizard__summary-choice';
          choice.textContent = `${itemLabel} (${tag})`;
          li.appendChild(part);
          li.appendChild(choice);
          this.summaryList.appendChild(li);
        });
        return true;
      }

      appendIncludedExtrasToForm(fragment) {
        const inc = this.getIncludedExtras();
        if (!inc) return;
        const tag = this.includedLabel();
        inc.items.forEach((itemLabel) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.setAttribute('data-cut-wizard-property', 'true');
          input.setAttribute('form', this.formId);
          input.name = this.propertyInputName(`${inc.heading} - ${itemLabel}`);
          input.value = tag;
          fragment.appendChild(input);
        });
      }

      pickDefaultOptionIndex(options) {
        if (!Array.isArray(options) || options.length === 0) return null;
        if (options.length === 1) return 0;
        const freeIdx = options.findIndex((opt) => (Number(opt?.extra_cents) || 0) === 0);
        return freeIdx >= 0 ? freeIdx : null;
      }

      /** @returns {number} option count for a step */
      _optionCount(step) {
        return Array.isArray(step?.options) ? step.options.length : 0;
      }


      _allSinglesReviewIndex() {
        return this.steps.length;
      }

      presetSingleOptionSelections() {
        this.steps.forEach((step, si) => {
          if (this._optionCount(step) === 1) {
            this._draftSelections[si] = 0;
          }
        });
      }

      findInitialModalStepIndex() {
        const n = this.steps.length;
        for (let i = 0; i < n; i++) {
          const c = this._optionCount(this.steps[i]);
          if (c === 0) return i;
        }
        for (let i = 0; i < n; i++) {
          if (this._optionCount(this.steps[i]) > 1) return i;
        }
        return this._allSinglesReviewIndex();
      }

      findNextUserFacingStepIndex(fromIndex) {
        const n = this.steps.length;
        for (let j = fromIndex + 1; j < n; j++) {
          const c = this._optionCount(this.steps[j]);
          if (c === 1) this._draftSelections[j] = 0;
          else if (c === 0 || c > 1) return j;
        }
        return null;
      }

      findPrevUserFacingStepIndex(fromIndex) {
        for (let j = fromIndex - 1; j >= 0; j--) {
          const c = this._optionCount(this.steps[j]);
          if (c === 0 || c > 1) return j;
        }
        return null;
      }

      countUserFacingSteps() {
        let k = 0;
        this.steps.forEach((step) => {
          const c = this._optionCount(step);
          if (c > 1) k += 1;
        });
        return k;
      }

      userFacingStepOrdinal(stepIndex) {
        if (stepIndex < 0 || stepIndex >= this.steps.length) return 0;
        if (this._optionCount(this.steps[stepIndex]) <= 1) return 0;
        let ord = 0;
        for (let i = 0; i <= stepIndex; i++) {
          if (this._optionCount(this.steps[i]) > 1) ord += 1;
        }
        return ord;
      }

      openModal(editMode) {
        if (!this.modal || !this.modalBody) return;
        this._draftSelections = new Array(this.steps.length).fill(null);
        if (editMode && this._savedSelections?.length === this.steps.length) {
          this._draftSelections = [...this._savedSelections];
        }
        this.presetSingleOptionSelections();
        this._currentIndex = this.findInitialModalStepIndex();
        this.renderStep();
        const opener = this.classList.contains('cut-wizard--empty')
          ? this.querySelector('[data-cut-wizard-open]')
          : this.querySelector('[data-cut-wizard-edit]');
        this.modal.show(opener || this.querySelector('[data-cut-wizard-open]'));
      }

      renderModalChrome() {
        if (this.modalTitleEl && this.config?.wizard_title) {
          this.modalTitleEl.textContent = this.config.wizard_title;
        }
      }

      renderStep() {
        if (!this.modalBody) return;

        const reviewIdx = this._allSinglesReviewIndex();
        if (this._currentIndex === reviewIdx) {
          const title = this.dataset.allSinglesTitle || 'Review selections';
          const sub =
            this.dataset.allSinglesSub ||
            'Each cut only had one option, so those were applied automatically. Save to continue.';
          const progress = this.dataset.progressLabel || 'Selection';
          const of = this.dataset.ofLabel || 'of';
          this.modalBody.innerHTML = `
            <p class="cut-wizard-modal__progress">${this.escapeHtml(progress)} 1 ${this.escapeHtml(of)} 1</p>
            <h3 class="cut-wizard-modal__step-heading" id="cut-wizard-step-h-${this.blockId}">${this.escapeHtml(title)}</h3>
            <p class="cut-wizard-modal__step-sub">${this.escapeHtml(sub)}</p>
          `;
          this.updateNavButtons();
          return;
        }

        const step = this.steps[this._currentIndex];
        if (!step) return;

        const heading = step.heading || `Step ${this._currentIndex + 1}`;
        const sub = step.subheading || '';
        const options = Array.isArray(step.options) ? step.options : [];
        const progress = this.dataset.progressLabel || 'Selection';
        const of = this.dataset.ofLabel || 'of';
        const totalFacing = this.countUserFacingSteps();
        const ord = this.userFacingStepOrdinal(this._currentIndex);
        const progressNum = totalFacing > 0 ? ord : this._currentIndex + 1;
        const progressDenom = totalFacing > 0 ? totalFacing : this.steps.length;

        if (options.length === 0) {
          this.modalBody.innerHTML = `<p class="cut-wizard-modal__progress">${this.escapeHtml(progress)} ${progressNum} ${this.escapeHtml(of)} ${progressDenom}</p>
          <h3 class="cut-wizard-modal__step-heading" id="cut-wizard-step-h-${this.blockId}">${this.escapeHtml(heading)}</h3>
          <p class="cut-wizard-modal__step-sub">${this.escapeHtml(
            this.dataset.emptyStepMessage || 'No options configured for this step.'
          )}</p>`;
          this.updateNavButtons();
          return;
        }

        if (options.length === 1) {
          this._draftSelections[this._currentIndex] = 0;
          const nxt = this.findNextUserFacingStepIndex(this._currentIndex);
          if (nxt != null) {
            this._currentIndex = nxt;
            this.renderStep();
            return;
          }
          const lastFacing = this.findPrevUserFacingStepIndex(this.steps.length);
          if (lastFacing != null) {
            this._currentIndex = lastFacing;
            this.renderStep();
            return;
          }
          this._currentIndex = reviewIdx;
          this.renderStep();
          return;
        }

        let selectedIdx = this._draftSelections[this._currentIndex];
        if (selectedIdx == null) {
          const autoIdx = this.pickDefaultOptionIndex(options);
          if (autoIdx != null) {
            this._draftSelections[this._currentIndex] = autoIdx;
            selectedIdx = autoIdx;
          }
        }

        this.modalBody.innerHTML = `
          <p class="cut-wizard-modal__progress">${this.escapeHtml(progress)} ${progressNum} ${this.escapeHtml(of)} ${progressDenom}</p>
          <h3 class="cut-wizard-modal__step-heading" id="cut-wizard-step-h-${this.blockId}">${this.escapeHtml(heading)}</h3>
          ${sub ? `<p class="cut-wizard-modal__step-sub">${this.escapeHtml(sub)}</p>` : ''}
          <div class="cut-wizard-modal__options" role="radiogroup" aria-labelledby="cut-wizard-step-h-${this.blockId}">
          ${options
            .map((opt, i) => {
              const label = opt.label || `Option ${i + 1}`;
              const extra = Number(opt.extra_cents) || 0;
              const extraLabel = this.formatExtra(extra);
              const checked = selectedIdx === i ? 'checked' : '';
              return `
                <label class="cut-wizard-modal__option">
                  <input type="radio" name="cut-wizard-step" value="${i}" ${checked} data-extra-cents="${extra}" />
                  <span class="cut-wizard-modal__option-card">
                    <span class="cut-wizard-modal__option-label">${this.escapeHtml(label)}</span>
                    ${extraLabel ? `<span class="cut-wizard-modal__option-extra">${this.escapeHtml(extraLabel)}</span>` : ''}
                  </span>
                </label>
              `;
            })
            .join('')}
          </div>
        `;

        this.modalBody.querySelectorAll('input[name="cut-wizard-step"]').forEach((input) => {
          input.addEventListener('change', () => {
            if (input.checked) {
              this._draftSelections[this._currentIndex] = parseInt(input.value, 10);
            }
            this.updateNavButtons();
          });
        });

        this.updateNavButtons();
      }

      escapeHtml(str) {
        return String(str)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;');
      }

      currentStepHasSelection() {
        const reviewIdx = this._allSinglesReviewIndex();
        if (this._currentIndex === reviewIdx) return this.completeDraft();
        const step = this.steps[this._currentIndex];
        if (!step) return false;
        const n = this._optionCount(step);
        if (n === 0) return false;
        if (n === 1) return true;
        return this._draftSelections[this._currentIndex] != null;
      }

      goStep(delta) {
        const reviewIdx = this._allSinglesReviewIndex();
        if (delta > 0) {
          if (this._currentIndex === reviewIdx) return;
          if (!this.currentStepHasSelection()) return;
          const nxt = this.findNextUserFacingStepIndex(this._currentIndex);
          if (nxt == null) return;
          this._currentIndex = nxt;
          this.renderStep();
          return;
        }
        if (delta < 0) {
          if (this._currentIndex === reviewIdx) {
            const prev = this.findPrevUserFacingStepIndex(this.steps.length);
            if (prev == null) return;
            this._currentIndex = prev;
            this.renderStep();
            return;
          }
          const prev = this.findPrevUserFacingStepIndex(this._currentIndex);
          if (prev == null) return;
          this._currentIndex = prev;
          this.renderStep();
        }
      }

      updateNavButtons() {
        if (!this.btnBack || !this.btnNext || !this.btnSave) return;
        const reviewIdx = this._allSinglesReviewIndex();

        if (this._currentIndex === reviewIdx) {
          this.btnBack.hidden = true;
          this.btnNext.hidden = true;
          this.btnNext.setAttribute('aria-hidden', 'true');
          this.btnSave.hidden = false;
          this.btnSave.setAttribute('aria-hidden', 'false');
          this.btnSave.toggleAttribute('disabled', !this.completeDraft());
          this.updateRunningTotal();
          return;
        }

        const initial = this.findInitialModalStepIndex();
        const firstFacing = initial !== reviewIdx ? initial : null;
        this.btnBack.hidden =
          this._currentIndex === reviewIdx || firstFacing == null || this._currentIndex === firstFacing;

        const hasMoreFacing = this.findNextUserFacingStepIndex(this._currentIndex) != null;
        const isLastFacing = !hasMoreFacing;
        this.btnNext.hidden = isLastFacing;
        this.btnNext.setAttribute('aria-hidden', isLastFacing ? 'true' : 'false');
        this.btnSave.hidden = !isLastFacing;
        this.btnSave.setAttribute('aria-hidden', !isLastFacing ? 'true' : 'false');
        this.btnNext.toggleAttribute('disabled', !this.currentStepHasSelection());
        this.btnSave.toggleAttribute('disabled', !this.completeDraft());
        this.updateRunningTotal();
      }

      completeDraft() {
        return this._draftSelections.length === this.steps.length && this._draftSelections.every((v) => v != null);
      }

      saveAndClose() {
        if (!this.completeDraft()) return;
        this._savedSelections = [...this._draftSelections];
        this.applySelectionsToForm();
        this.updateSummaryDisplay();
        this.syncAddToCartGate();
        this.modal?.hide();
      }

      propertyInputName(heading) {
        return `properties[${this.propertyPrefix} - ${heading}]`;
      }

      clearPropertyInputs() {
        this.querySelectorAll('input[data-cut-wizard-property]').forEach((el) => el.remove());
      }

      applySelectionsToForm() {
        this.clearPropertyInputs();
        if (!this._savedSelections) return;

        let extras = 0;
        const fragment = document.createDocumentFragment();

        this.steps.forEach((step, si) => {
          const optIdx = this._savedSelections[si];
          const opt = step.options?.[optIdx];
          if (!opt) return;
          const heading = step.heading || `Part ${si + 1}`;
          const label = opt.label || '';
          const extra = Number(opt.extra_cents) || 0;
          extras += extra;

          const input = document.createElement('input');
          input.type = 'hidden';
          input.setAttribute('data-cut-wizard-property', 'true');
          input.setAttribute('form', this.formId);
          input.name = this.propertyInputName(heading);
          input.value = extra > 0 ? `${label} (+${this.formatMoney(extra)})` : label;
          fragment.appendChild(input);
        });

        this.appendIncludedExtrasToForm(fragment);

        const totalLine = document.createElement('input');
        totalLine.type = 'hidden';
        totalLine.setAttribute('data-cut-wizard-property', 'true');
        totalLine.setAttribute('form', this.formId);
        totalLine.name = 'properties[Cut selections - surcharges total]';
        totalLine.value = this.formatMoney(extras);
        fragment.appendChild(totalLine);

        const centsLine = document.createElement('input');
        centsLine.type = 'hidden';
        centsLine.setAttribute('data-cut-wizard-property', 'true');
        centsLine.setAttribute('form', this.formId);
        centsLine.name = 'properties[Cut selections - surcharges (cents)]';
        centsLine.value = String(extras);
        fragment.appendChild(centsLine);

        this.appendChild(fragment);
      }

      updateSummaryDisplay() {
        const hasSaved = this._savedSelections && this.completeDraftFromSaved();
        this.classList.toggle('cut-wizard--empty', !hasSaved);
        if (this.summaryEmpty) this.summaryEmpty.hidden = hasSaved;

        if (!this.summaryList) return;
        this.summaryList.innerHTML = '';

        if (!hasSaved) {
          if (this.totalsRow) this.totalsRow.hidden = true;
          if (this.summaryFootnote) {
            this.summaryFootnote.hidden = true;
            this.summaryFootnote.textContent = '';
          }
          return;
        }

        let extras = 0;
        let anySingleOptionOnly = false;
        this.steps.forEach((step, si) => {
          const optIdx = this._savedSelections[si];
          const opt = step.options?.[optIdx];
          if (!opt) return;
          extras += Number(opt.extra_cents) || 0;
          const singleOnly = this._optionCount(step) === 1;
          if (singleOnly) anySingleOptionOnly = true;
          const li = document.createElement('li');
          const part = document.createElement('span');
          part.className = 'cut-wizard__summary-part';
          part.textContent = step.heading || `Part ${si + 1}`;
          const choice = document.createElement('span');
          choice.className = 'cut-wizard__summary-choice';
          const extra = Number(opt.extra_cents) || 0;
          let line = extra > 0 ? `${opt.label} (+${this.formatMoney(extra)})` : opt.label;
          if (singleOnly) line += ' *';
          choice.textContent = line;
          li.appendChild(part);
          li.appendChild(choice);
          this.summaryList.appendChild(li);
        });

        this.appendIncludedSummaryRows();

        const base = parseInt(this.dataset.basePriceCents || '0', 10) || 0;
        if (this.packPriceEl) this.packPriceEl.textContent = this.formatMoney(base);
        if (this.totalExtrasEl) this.totalExtrasEl.textContent = this.formatMoney(extras);
        if (this.totalCombinedEl) this.totalCombinedEl.textContent = this.formatMoney(base + extras);
        if (this.totalsRow) this.totalsRow.hidden = false;

        if (this.summaryFootnote) {
          const note = (this.dataset.singleFootnote || '*Cannot be changed').trim();
          if (anySingleOptionOnly && note) {
            this.summaryFootnote.textContent = note;
            this.summaryFootnote.hidden = false;
          } else {
            this.summaryFootnote.hidden = true;
            this.summaryFootnote.textContent = '';
          }
        }

        const summaryWrap = this.querySelector('[data-cut-wizard-summary-wrap]');
        if (summaryWrap instanceof HTMLDetailsElement) {
          const collapseDefault = this.steps.length > 5;
          summaryWrap.open = !collapseDefault;
        }
      }

      completeDraftFromSaved() {
        return (
          this._savedSelections &&
          this._savedSelections.length === this.steps.length &&
          this._savedSelections.every((v) => v != null)
        );
      }

      setAddToCartGate(disabled) {
        this.refreshProductFormRef();
        if (!this.productForm || typeof this.productForm.toggleSubmitButton !== 'function') return;
        if (!this.isVariantPurchasable()) return;
        const msg = disabled ? this.dataset.requiredMessage || 'Select cuts to continue' : null;
        this.productForm.toggleSubmitButton(disabled, msg);
      }
    }
  );
}
